/*
 * Editing a saved payment (PAY-018, owner 2026-10-23: "saved payments should
 * get an Edit option"). The New payment form opens pre-filled; these helpers
 * turn the saved payment + the client's invoices into the form's rows and the
 * form back into the `update-payment` request.
 *
 * "Balance" in the edit rows is what the invoice would owe WITHOUT this
 * payment (its balance now + what this payment put on it), so ticking an
 * invoice fills in the right amount and the overpayment question compares
 * with the right number (the server does the same, backend `PaymentEdit`).
 */
import { lineAmount } from "./paymentAmounts.utils";

export type PaymentFormRow = {
  isChecked: boolean;
  /** Invoice number shown in the row. */
  invoiceId: number;
  /** Invoice id (GUID). */
  id: string;
  dueDate: string;
  invoiceDate?: string;
  originalAmount: number;
  balance: number;
  amountPaid: string;
  /** What this payment already paid on the invoice (edit only). */
  previousAmount?: number;
};

export type EditableInvoice = {
  id?: string | null;
  invoiceNo?: number | null;
  dueDate?: string | null;
  invoiceDate?: string | null;
  total?: number | null;
  balance?: number | null;
};

export type SavedPaymentLine = {
  invoiceId?: string | null;
  invoiceNo?: number | null;
  invoiceDueDate?: string | null;
  invoiceTotal?: number | null;
  invoiceBalance?: number | null;
  amountPaid?: number | null;
};

const round2 = (value: number) => Math.round(value * 100) / 100;

const amountText = (value: number) => (value > 0 ? String(round2(value)) : "");

/** This payment's amount per invoice id (several lines for one invoice added up). */
export const previousAmounts = (
  lines: SavedPaymentLine[] | null | undefined,
): Record<string, number> => {
  const result: Record<string, number> = {};
  (lines || []).forEach((line) => {
    if (!line?.invoiceId) return;
    result[line.invoiceId] = round2(
      (result[line.invoiceId] || 0) + (line.amountPaid || 0),
    );
  });
  return result;
};

/** Rows for the client's invoices, with this payment's amounts filled in. */
export const paymentFormRows = (
  invoices: EditableInvoice[] | null | undefined,
  savedLines?: SavedPaymentLine[] | null,
): PaymentFormRow[] => {
  const previous = previousAmounts(savedLines);
  const rows: PaymentFormRow[] = (invoices || []).map((invoice) => {
    const id = invoice?.id || "";
    const before = previous[id] || 0;
    return {
      invoiceId: invoice?.invoiceNo || 0,
      id,
      dueDate: invoice?.dueDate || "",
      invoiceDate: invoice?.invoiceDate || "",
      originalAmount: invoice?.total || 0,
      balance: round2((invoice?.balance || 0) + before),
      amountPaid: amountText(before),
      isChecked: before > 0,
      previousAmount: before,
    };
  });

  // Lines on invoices that aren't in the client's list (e.g. another client's
  // invoice): keep them so saving doesn't remove them by accident. Their
  // balance is the one saved with the line (+ this payment), the server checks
  // the real one.
  const listed = new Set(rows.map((row) => row.id));
  const extra = new Map<string, PaymentFormRow>();
  (savedLines || []).forEach((line) => {
    const id = line?.invoiceId || "";
    if (!id || listed.has(id) || extra.has(id)) return;
    const before = previous[id] || 0;
    const sameInvoice = (savedLines || []).filter((l) => l?.invoiceId === id);
    const lastBalance = sameInvoice[sameInvoice.length - 1]?.invoiceBalance;
    extra.set(id, {
      invoiceId: line?.invoiceNo || 0,
      id,
      dueDate: line?.invoiceDueDate || "",
      originalAmount: line?.invoiceTotal || 0,
      balance: round2((lastBalance || 0) + before),
      amountPaid: amountText(before),
      isChecked: before > 0,
      previousAmount: before,
    });
  });
  return [...rows, ...Array.from(extra.values())];
};

export type UpdatePaymentInput = {
  paymentId: string;
  customerId: string;
  paymentDate: Date;
  paymentMethodId: string;
  notes: string;
  paymentStatusId: string | null;
  rows: PaymentFormRow[];
  allowOverpayment: boolean;
};

/** The `update-payment` body: the WHOLE split (replacePaymentItems). */
export const updatePaymentBody = (input: UpdatePaymentInput) => {
  const paymentItem = input.rows
    .filter((row) => lineAmount(row.amountPaid) > 0)
    .map((row) => ({
      invoiceId: row.id,
      amountPaid: round2(lineAmount(row.amountPaid)),
    }));
  return {
    id: input.paymentId,
    customerId: input.customerId,
    paymentDate: input.paymentDate.toISOString(),
    paymentMethodId: input.paymentMethodId,
    notes: input.notes,
    paymentStatusId: input.paymentStatusId,
    amount: round2(paymentItem.reduce((sum, item) => sum + item.amountPaid, 0)),
    balance: round2(input.rows.reduce((sum, row) => sum + row.balance, 0)),
    allowOverpayment: input.allowOverpayment,
    replacePaymentItems: true,
    paymentItem,
  };
};
